<template>
  <AuthWelcomeShell
    compact
    title="Welcome aboard"
    lead="We're so excited to have you at Symphony. Share a few details and we'll get everything set up for your company. We'll set you up as the administrator. You can invite the rest of your team whenever you're ready."
  >
    <OnboardingScaffoldBanner compact />

    <div
      class="grid grid-cols-1 gap-2.5 md:grid-cols-2 md:gap-x-3 md:gap-y-2.5"
    >
      <div class="min-w-0">
        <label
          for="company_name"
          class="mb-1 block text-xs font-medium text-stone-800 sm:text-sm"
        >Company <span class="text-red-500">*</span></label>
        <IconField>
          <InputIcon class="pi pi-building" />
          <InputText
            id="company_name"
            v-model="companyName"
            type="text"
            placeholder="Company name"
            class="w-full"
            :class="{ 'p-invalid': !!fieldErrors.company_name }"
            autocomplete="organization"
            @update:model-value="clearFieldError('company_name')"
          />
        </IconField>
        <p
          v-if="fieldErrors.company_name"
          class="mt-1 text-xs text-red-600"
        >
          {{ fieldErrors.company_name }}
        </p>
      </div>

      <div class="min-w-0">
        <label
          for="email"
          class="mb-1 block text-xs font-medium text-stone-800 sm:text-sm"
        >Work email <span class="text-red-500">*</span></label>
        <IconField>
          <InputIcon class="pi pi-envelope" />
          <InputText
            id="email"
            v-model="email"
            type="email"
            placeholder="you@company.com"
            class="w-full"
            :class="{ 'p-invalid': !!fieldErrors.email }"
            autocomplete="email"
            @update:model-value="clearFieldError('email')"
          />
        </IconField>
        <p
          v-if="fieldErrors.email"
          class="mt-1 text-xs text-red-600"
        >
          {{ fieldErrors.email }}
        </p>
      </div>

      <div class="min-w-0">
        <label
          for="first_name"
          class="mb-1 block text-xs font-medium text-stone-800 sm:text-sm"
        >First name <span class="text-red-500">*</span></label>
        <IconField>
          <InputIcon class="pi pi-user" />
          <InputText
            id="first_name"
            v-model="firstName"
            type="text"
            placeholder="First name"
            class="w-full"
            :class="{ 'p-invalid': !!fieldErrors.first_name }"
            autocomplete="given-name"
            @update:model-value="clearFieldError('first_name')"
          />
        </IconField>
        <p
          v-if="fieldErrors.first_name"
          class="mt-1 text-xs text-red-600"
        >
          {{ fieldErrors.first_name }}
        </p>
      </div>

      <div class="min-w-0">
        <label
          for="last_name"
          class="mb-1 block text-xs font-medium text-stone-800 sm:text-sm"
        >Last name <span class="text-red-500">*</span></label>
        <IconField>
          <InputIcon class="pi pi-user" />
          <InputText
            id="last_name"
            v-model="lastName"
            type="text"
            placeholder="Last name"
            class="w-full"
            :class="{ 'p-invalid': !!fieldErrors.last_name }"
            autocomplete="family-name"
            @update:model-value="clearFieldError('last_name')"
          />
        </IconField>
        <p
          v-if="fieldErrors.last_name"
          class="mt-1 text-xs text-red-600"
        >
          {{ fieldErrors.last_name }}
        </p>
      </div>

      <div
        class="min-w-0 space-y-2.5 md:col-span-2"
      >
        <div
          class="grid grid-cols-1 gap-2.5 md:grid-cols-2 md:gap-x-3"
        >
          <div class="min-w-0">
            <label
              for="password"
              class="mb-1 block text-xs font-medium text-stone-800 sm:text-sm"
            >Password <span class="text-red-500">*</span></label>
            <IconField>
              <InputIcon class="pi pi-lock" />
              <InputText
                id="password"
                v-model="password"
                type="password"
                placeholder="10+ characters, mixed case, number, symbol"
                class="w-full"
                :class="{ 'p-invalid': !!fieldErrors.password }"
                autocomplete="new-password"
                aria-describedby="register-password-hint"
                @update:model-value="clearFieldError('password')"
              />
            </IconField>
          </div>

          <div class="min-w-0">
            <label
              for="password_confirmation"
              class="mb-1 block text-xs font-medium text-stone-800 sm:text-sm"
            >Confirm <span class="text-red-500">*</span></label>
            <IconField>
              <InputIcon class="pi pi-lock" />
              <InputText
                id="password_confirmation"
                v-model="passwordConfirmation"
                type="password"
                placeholder="Repeat password"
                class="w-full"
                :class="{ 'p-invalid': !!fieldErrors.password_confirmation }"
                autocomplete="new-password"
                @update:model-value="clearFieldError('password_confirmation')"
              />
            </IconField>
            <p
              v-if="fieldErrors.password_confirmation"
              class="mt-1 text-xs text-red-600"
            >
              {{ fieldErrors.password_confirmation }}
            </p>
          </div>
        </div>

        <p
          id="register-password-hint"
          class="w-full max-w-none text-xs leading-relaxed text-stone-600"
        >
          Use at least 10 characters, including uppercase and lowercase letters, at least one number, and one symbol (for example !, @, #, or %).
        </p>
        <p
          v-if="fieldErrors.password"
          class="w-full max-w-none text-xs text-red-600"
        >
          {{ fieldErrors.password }}
        </p>
      </div>
    </div>

    <div
      v-if="errorMessage"
      class="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-800 sm:text-sm whitespace-pre-wrap"
    >
      {{ errorMessage }}
    </div>

    <Button
      label="Create my company"
      icon="pi pi-sparkles"
      icon-pos="right"
      class="w-full"
      size="small"
      @click="submit"
    />

    <template #after>
      <span>Already have an account?</span>
      <RouterLink
        to="/login"
        class="ml-1 font-semibold text-amber-900 underline decoration-amber-300 underline-offset-2 hover:text-amber-950"
      >
        Sign in
      </RouterLink>
    </template>
  </AuthWelcomeShell>
</template>

<script setup>
import { ref } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import OnboardingScaffoldBanner from '@/components/auth/OnboardingScaffoldBanner.vue';
import AuthWelcomeShell from '@/components/auth/AuthWelcomeShell.vue';
import { initOnboardingFromRegistration } from '@/composables/useOnboardingWizardState';
import { saveOnboardingCredentials } from '@/config/onboardingCredentials';
import { validateRegisterCompanyForm } from '@/utils/registerCompanyFormValidation';

const router = useRouter();

const companyName = ref('');
const firstName = ref('');
const lastName = ref('');
const email = ref('');
const password = ref('');
const passwordConfirmation = ref('');
const errorMessage = ref('');
const fieldErrors = ref({
  company_name: '',
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  password_confirmation: '',
});

function clearFieldError(key) {
  if (fieldErrors.value[key]) {
    fieldErrors.value = { ...fieldErrors.value, [key]: '' };
  }
}

const submit = () => {
  errorMessage.value = '';
  const result = validateRegisterCompanyForm({
    company_name: companyName.value,
    first_name: firstName.value,
    last_name: lastName.value,
    email: email.value,
    password: password.value,
    password_confirmation: passwordConfirmation.value,
  });

  fieldErrors.value = {
    company_name: result.fieldErrors.company_name ?? '',
    first_name: result.fieldErrors.first_name ?? '',
    last_name: result.fieldErrors.last_name ?? '',
    email: result.fieldErrors.email ?? '',
    password: result.fieldErrors.password ?? '',
    password_confirmation: result.fieldErrors.password_confirmation ?? '',
  };

  if (!result.valid) {
    errorMessage.value = 'Please fix the highlighted fields.';
    return;
  }

  initOnboardingFromRegistration({
    company_name: result.values.company_name,
    first_name: result.values.first_name,
    last_name: result.values.last_name,
    email: result.values.email,
  });

  saveOnboardingCredentials({
    password: result.values.password,
    password_confirmation: result.values.password_confirmation,
  });

  router.push({ name: 'OnboardingCompany' });
};
</script>
